// PostCard.jsx
import React, { useState } from "react";
import Avatar from "../common/Avatar";
import Icon from "../common/Icon";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");

const timeAgo = (date) => {
  const s = Math.floor((Date.now() - new Date(date)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

const avSrc = (p) =>
  !p
    ? "/assets/images/avatar-brother-julian.png"
    : p.startsWith("/uploads")
      ? `${API_ROOT}${p}`
      : p;

const mediaSrc = (p) =>
  p?.startsWith("/uploads") ? `${API_ROOT}${p}` : p;

const isImage = (p) => /\.(jpe?g|png|gif|webp|jfif|avif|svg)$/i.test(p);

// ─── Dummy Posts Data (Before Posts - 2 regular, 1 testimony) ────────────────
const dummyPosts = [
  {
    id: "dummy-before-1",
    type: "testimony",
    author: {
      id: "dummy-author-1",
      name: "Yusuf Ibrahim",
      avatar: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    content: "SubhanAllah! Just completed reading Surah Al-Kahf this Friday. The story of Dhul-Qarnayn always amazes me - how he used the power Allah gave him for justice and goodness. Reminds us that all abilities are tests from Allah. How we use them matters more than what we have.",
    tag: "Quran testimony",
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    isBlessedByMe: false,
    engagement: { blessCount: 45, commentCount: 8 }
  },
  {
    id: "dummy-before-2",
    type: "testimony",
    author: {
      id: "dummy-author-2",
      name: "Mariam Hassan",
      avatar: "https://randomuser.me/api/portraits/women/45.jpg"
    },
    content: "Assalamu Alaikum dear brothers and sisters. My uncle is in the ICU after a heart attack. Doctors are not very hopeful. Please make sincere dua for his recovery. His name is Abdul Rahman. JazakAllah Khair for your prayers. May Allah accept our duas.",
    tag: "Urgent Prayer Needed",
    createdAt: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
    isBlessedByMe: false,
    engagement: { blessCount: 189, commentCount: 42 }
  },
  {

    id: "dummy-after-1",
    type: "testimony",
    author: {
      id: "dummy-author-3",
      name: "Khadija Bint Ahmed",
      avatar: "https://randomuser.me/api/portraits/women/68.jpg"
    },
    beforeContent: "Before joining this community 6 months ago, I was struggling with my hijab journey. I felt insecure and worried about what people would say. I would put it on and take it off constantly. My iman was weak and I felt like a hypocrite.",
    afterContent: "Alhamdulillah! Today I wear my hijab with pride and confidence. The sisters here supported me, shared their stories, and encouraged me. I've learned that hijab is between me and Allah, not me and society. Now I feel protected and closer to Allah than ever before.",
    beforeAttachment: null,
    afterAttachment: null,
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    isBlessedByMe: true,
    engagement: { blessCount: 324, commentCount: 67 }
  }
];

// ─── Testimony Layout ────────────────────────────────────────────────────────
const TestimonyCard = ({ post, blessCount, blessed, onBless, commentCount, onToggleComments, showComments, comments, loadingComments, newComment, setNewComment, onSubmitComment, isOwner, onDelete, user }) => (
  <div className="bg-white rounded-card border border-[#1F3A600D] shadow-[0px_1.43px_2.87px_0px_#0000000D] overflow-hidden mb-4">
    <div className="p-4 sm:p-5 pb-3">

      {/* Author row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Avatar src={avSrc(post.author?.avatar)} alt={post.author?.name} size={44} className="flex-shrink-0 sm:w-[50px] sm:h-[50px]" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[15px] sm:text-[17px] font-bold text-[#1F3A60] font-body truncate">
                {post.author?.name}
              </p>
            </div>
            <p className="font-['Inter'] text-[12px] sm:text-[14px] text-[#657786]">
              {post.createdAt ? timeAgo(post.createdAt) : post.timestamp || ""}
            </p>
          </div>
        </div>
        {isOwner && (
          <button onClick={onDelete} className="text-gray-300 hover:text-red-400 flex-shrink-0 ml-2">
            <Icon name="more" size={18} />
          </button>
        )}
      </div>

      {/* Testimony badge - 12px as requested */}
      <div className="mb-4">
        <span className="inline-block bg-[#1F3A60] text-white text-[12px] font-semibold px-3 py-1 rounded-full font-body tracking-wide">
          Testimony
        </span>
      </div>

      {/* Before / After columns */}
      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        {/* Before */}
        <div>
          <p className="text-[14px] sm:text-[18px] font-bold text-[#1F3A60] font-body mb-2">Before</p>
          <p className="text-[14px] sm:text-[16px] text-gray-600 leading-[1.6] font-body whitespace-pre-wrap">
            {post.before_text}
          </p>
          {post.before_media && (
            <div className="mt-3 rounded-xl overflow-hidden">
              {isImage(post.before_media) ? (
                <img src={mediaSrc(post.before_media)} alt="" className="w-full max-h-[200px] object-cover rounded-xl" />
              ) : (
                <video src={mediaSrc(post.before_media)} controls className="w-full max-h-[200px] rounded-xl" />
              )}
            </div>
          )}
        </div>

        {/* After */}
        <div>
          <p className="text-[14px] sm:text-[18px] font-bold text-[#1F3A60] font-body mb-2">After</p>
          <p className="text-[14px] sm:text-[16px] text-gray-600 leading-[1.6] font-body whitespace-pre-wrap">
            {post.after_text || ""}
          </p>
          {post.after_media && (
            <div className="mt-3 rounded-xl overflow-hidden">
              {isImage(post.after_media) ? (
                <img src={mediaSrc(post.after_media)} alt="" className="w-full max-h-[200px] object-cover rounded-xl" />
              ) : (
                <video src={mediaSrc(post.after_media)} controls className="w-full max-h-[200px] rounded-xl" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Engagement */}
    <EngagementBar
      blessed={blessed}
      blessCount={blessCount}
      commentCount={commentCount}
      onBless={onBless}
      onToggleComments={onToggleComments}
    />

    {/* Comments */}
    {showComments && (
      <CommentsSection
        comments={comments}
        loadingComments={loadingComments}
        newComment={newComment}
        setNewComment={setNewComment}
        onSubmit={onSubmitComment}
        user={user}
      />
    )}
  </div>
);

// ─── Shared sub-components ───────────────────────────────────────────────────
const EngagementBar = ({ blessed, blessCount, commentCount, onBless, onToggleComments }) => (
  <div className="flex items-center gap-3 sm:gap-6 px-4 sm:px-5 py-3 border-t border-gray-50 flex-wrap">
    <button
      onClick={onBless}
      className={`flex items-center gap-1.5 text-[12px] sm:text-[13px] font-semibold transition-all ${blessed ? "text-gold" : "text-gray-400 hover:text-gold"
        }`}
    >
      🤲 {blessCount} Bless
    </button>
    <button
      onClick={onToggleComments}
      className="flex items-center gap-1.5 text-[12px] sm:text-[13px] text-gray-500 hover:text-primary"
    >
      <Icon name="chat" size={15} className="text-gray-400" />
      {commentCount} Comments
    </button>
    {/* <button className="text-gray-400 hover:text-primary">
      <Icon name="send" size={15} />
    </button> */}
  </div>
);

const CommentsSection = ({ comments, loadingComments, newComment, setNewComment, onSubmit, user }) => (
  <div className="border-t border-gray-100 px-4 sm:px-5 py-4">
    {loadingComments ? (
      <p className="text-[13px] text-gray-400 text-center py-2">Loading...</p>
    ) : (
      <>
        {comments.length === 0 && (
          <p className="text-[13px] text-gray-400 text-center py-2">No comments yet.</p>
        )}
        <div className="flex flex-col gap-3 mb-4">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <Avatar src={avSrc(c.author?.avatar)} alt={c.author?.name} size={28} className="flex-shrink-0" />
              <div className="flex-1 bg-gray-50 rounded-xl px-3 py-2 min-w-0">
                <p className="text-[12px] font-bold text-gray-800">{c.author?.name}</p>
                <p className="text-[12px] sm:text-[13px] text-gray-600 leading-snug break-words">{c.content}</p>
              </div>
            </div>
          ))}
        </div>
        {user && (
          <form onSubmit={onSubmit} className="flex gap-2">
            <Avatar src={avSrc(user.avatar)} alt={user.name} size={28} className="flex-shrink-0" />
            <input
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Write a comment..."
              className="flex-1 px-3 py-2 bg-gray-50 rounded-full text-[12px] sm:text-[13px] outline-none focus:bg-gray-100 min-w-0"
            />
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-primary text-white rounded-full text-[11px] sm:text-[12px] font-semibold disabled:opacity-40 flex-shrink-0"
            >
              Post
            </button>
          </form>
        )}
      </>
    )}
  </div>
);

// ─── Main PostCard ────────────────────────────────────────────────────────────
const PostCard = ({ post, onDelete }) => {
  const { user } = useAuth();
  const [blessed, setBlessed] = useState(post.isBlessedByMe || false);
  const [blessCount, setBlessCount] = useState(post.engagement?.blessCount || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [commentCount, setCommentCount] = useState(post.engagement?.commentCount || 0);

  const author = post.author || {};
  const isOwner = user && author.id === user.id;
  const isTestimony = post.type === "testimony";

  const handleBless = async () => {
    if (!user) return;
    try {
      const d = await api.toggleBless(post.id);
      setBlessed(d.blessed);
      setBlessCount((c) => (d.blessed ? c + 1 : c - 1));
    } catch {
      // Fallback for dummy data
      setBlessed(!blessed);
      setBlessCount((c) => (blessed ? c - 1 : c + 1));
    }
  };

  const loadComments = async () => {
    if (showComments) { setShowComments(false); return; }
    setLoadingComments(true);
    try {
      const d = await api.getComments(post.id);
      setComments(d.comments || []);
      setShowComments(true);
    } catch {
      // Fallback for dummy data
      setComments([]);
      setShowComments(true);
    }
    setLoadingComments(false);
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;
    try {
      const result = await api.createComment(post.id, newComment.trim());
      if (!result.flagged) {
        setComments((prev) => [...prev, result.comment]);
        setCommentCount((c) => c + 1);
      }
              setNewComment("");

    } catch {
      // Fallback for dummy data
      // const dummyComment = {
      //   id: Date.now(),
      //   author: { name: user.name, avatar: user.avatar },
      //   content: newComment.trim()
      // };
      // setComments((prev) => [...prev, dummyComment]);
      // setCommentCount((c) => c + 1);
      // setNewComment("");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.deletePost(post.id);
      onDelete?.(post.id);
    } catch {
      onDelete?.(post.id);
    }
  };

  const sharedProps = {
    post, blessCount, blessed, onBless: handleBless,
    commentCount, onToggleComments: loadComments,
    showComments, comments, loadingComments,
    newComment, setNewComment, onSubmitComment: submitComment,
    isOwner, onDelete: handleDelete, user,
  };

  // ── Testimony layout ──
  if (isTestimony) return <TestimonyCard {...sharedProps} />;

  // ── Normal layout ──
  return (
    <div className="bg-white rounded-card border border-[#1F3A600D] shadow-[0px_1.43px_2.87px_0px_#0000000D] overflow-hidden mb-4">
      <div className="p-4 sm:p-5 pb-3">

        {/* Author row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Avatar src={avSrc(author.avatar)} alt={author.name} size={44} className="flex-shrink-0 sm:w-[57px] sm:h-[57px]" />
            <div className="min-w-0">
              <p className="text-[15px] sm:text-[18px] md:text-[20px] font-bold text-[#1F3A60] truncate font-body">
                {author.name}
              </p>
              <p className="text-[12px] sm:text-[14px] text-[#64748B]">
                {post.createdAt ? timeAgo(post.createdAt) : post.timestamp || ""}
                {post.tag && (
                  <> · <span className="text-primary font-medium">{post.tag}</span></>
                )}
              </p>
            </div>
          </div>
          {isOwner && (
            <button onClick={handleDelete} className="text-gray-300 hover:text-red-400 flex-shrink-0 ml-2">
              <Icon name="more" size={18} />
            </button>
          )}
        </div>

        {/* Post type badge (Prayer, Reflection, etc.) */}
        {post.type && post.type !== "default" && (
          <div className="mb-3">
            <span className="inline-block bg-[#1F3A60] text-white text-[11px] font-semibold px-3 py-1 rounded-full font-body capitalize">
              {post.type.replace('_', ' ')}
            </span>
          </div>
        )}

        {/* Content - 16px regular Inter as requested */}
        <p className="text-[16px] text-gray-700 leading-[1.6] font-body whitespace-pre-wrap font-normal">
          {post.content}
        </p>
      </div>

      {/* Attachment */}
      {post.attachment && (
        <div className="relative">
          {isImage(post.attachment) ? (
            <img
              src={mediaSrc(post.attachment)}
              alt=""
              className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
            />
          ) : (
            <video
              src={mediaSrc(post.attachment)}
              controls
              className="w-full max-h-[280px] sm:max-h-[360px] md:max-h-[420px] object-cover"
            />
          )}
        </div>
      )}

      <EngagementBar
        blessed={blessed}
        blessCount={blessCount}
        commentCount={commentCount}
        onBless={handleBless}
        onToggleComments={loadComments}
      />

      {showComments && (
        <CommentsSection
          comments={comments}
          loadingComments={loadingComments}
          newComment={newComment}
          setNewComment={setNewComment}
          onSubmit={submitComment}
          user={user}
        />
      )}
    </div>
  );
};

// ─── Simple Wrapper Component ────────────────────────────────────────
const PostList = ({ posts, onDeletePost }) => {
  const [visibleDummyPosts, setVisibleDummyPosts] = useState(dummyPosts.map(p => p.id));

  const handleDeleteDummy = (postId) => {
    setVisibleDummyPosts(prev => prev.filter(id => id !== postId));
  };

  const activeDummyPosts = dummyPosts.filter(p => visibleDummyPosts.includes(p.id));

  return (
    <div className="flex flex-col gap-4">
      {/* 3 Dummy Posts at the top */}
      {activeDummyPosts.map((dummyPost) => (
        <PostCard
          key={dummyPost.id}
          post={dummyPost}
          onDelete={() => handleDeleteDummy(dummyPost.id)}
        />
      ))}

      {/* Original Posts from API */}
      {posts?.map((post) => (
        <PostCard key={post.id} post={post} onDelete={onDeletePost} />
      ))}
    </div>
  );
};

export { PostCard, PostList };
export default PostCard;